/**
 * Writing one prompt.
 *
 * THE WHOLE FLOW IS: type the instruction, press 保存する. It is live from that
 * moment - the next article of this kind is written from it. No draft state to
 * promote, no evaluation to run first, no second re-authentication.
 *
 * WHAT WAS HERE BEFORE, and why it is gone. Shipping a wording used to mean
 * registering an evaluation case, queueing a run, waiting for a verdict, then
 * activating while quoting that run's id behind a step-up prompt. Every step was
 * defensible in isolation. Together they meant nothing ever shipped: the whole
 * catalogue sat unreleased and every clinic ran on the wording compiled into the
 * server, which no operator had ever read. A gate nobody passes is not a gate.
 *
 * WHAT REPLACED IT is not "nothing" - it is the checks that were always doing
 * the real work, in the place they actually apply:
 *
 *   the lint runs as the instruction is typed, and refuses a save only for what
 *   would break generation outright (an empty instruction, a {{placeholder}} the
 *   code never supplies). Everything else - a missing 医療広告 clause, a thin
 *   rule set - is shown as a note. A wording is not a violation;
 *
 *   the article that comes out is checked on its own, free, every time, before
 *   anybody approves it. That check does not care which prompt wrote the draft,
 *   which is exactly why gating the prompt was doing the same work one layer
 *   removed from where it matters;
 *
 *   every save is a new immutable version and the previous one is retired, not
 *   overwritten - so 「どの指示でこの記事が書かれたか」 stays answerable and one
 *   click puts the old wording back;
 *
 *   and the audit trail records who saved what, when.
 *
 * The preview and the diff stay because they are free and they are what an
 * author actually uses: one shows the real assembled prompt against an invented
 * clinic, the other shows what this save changes about the live wording.
 */
import { useEffect, useState } from 'react';
import { Link, useLoaderData, useNavigate, useRevalidator } from 'react-router';
import { ApiError, del, post, type AdminPromptVersions } from '../api.ts';
import { Card, Empty, Pill, jaDate } from '../components/ui.tsx';
import { DangerButton } from '../components/DangerButton.tsx';
import { PromptComposer } from '../components/PromptComposer.tsx';
import { diffCounts, diffLines } from '../diff.ts';

/** The six section shapes, in the order the plan screen offers them. */
const SECTION_KINDS = [
  ['body', '本文'],
  ['summary', '冒頭まとめ'],
  ['checklist', 'チェックリスト'],
  ['steps', '施術の流れ'],
  ['reasons', '選ばれる理由'],
  ['faq', 'よくある質問'],
] as const;

interface LoaderData {
  versions: AdminPromptVersions;
}

/** Both picture uses: the whole instruction is the template. See migration 093. */
const isImageTask = (taskType: string) => taskType === 'image' || taskType === 'article_image';

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'muted'> = {
  ACTIVE: 'ok',
  IN_REVIEW: 'warn',
  DRAFT: 'warn',
  ROLLED_BACK: 'muted',
};

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: '使用中',
  IN_REVIEW: 'レビュー中',
  DRAFT: '下書き',
  ROLLED_BACK: '旧版',
};

interface Finding {
  level: 'error' | 'warn';
  code: string;
  message: string;
}

interface Preview {
  system: string;
  user: string;
  tokens: number;
  model: { label: string; model: string } | null;
  inputMicroCost: number | null;
  /** What each variable resolved to against the fixture. */
  variables: Record<string, string>;
}

/**
 * What the checks found, and what each one costs the author.
 *
 * An error stops the save because the instruction would not work - an empty
 * body, a placeholder nothing fills. A warning is a note: the wording may be
 * thinner than house style wants, and that is the author's call to make, not
 * this screen's. Saying which is which is the difference between a panel
 * somebody reads and a panel somebody learns to click past.
 */
function Findings({ findings }: { findings: Finding[] }) {
  if (findings.length === 0) {
    return (
      <p className="muted small" style={{ margin: '6px 0 0' }}>
        <Pill tone="ok">問題なし</Pill> 指示の内容に不足はありません。
      </p>
    );
  }
  return (
    <ul className="findings">
      {findings.map((finding) => (
        <li key={finding.code} className={`findings__item findings__item--${finding.level}`}>
          <Pill tone={finding.level === 'error' ? 'danger' : 'warn'}>
            {finding.level === 'error' ? '保存不可' : '確認'}
          </Pill>
          <span>{finding.message}</span>
        </li>
      ))}
    </ul>
  );
}

function Diff({ before, after }: { before: string; after: string }) {
  const lines = diffLines(before, after);
  const { added, removed } = diffCounts(lines);
  if (added === 0 && removed === 0) {
    return <p className="muted small">使用中の内容と同じです。</p>;
  }
  return (
    <>
      <p className="muted small" style={{ marginTop: 0 }}>
        追加 {added} 行・削除 {removed} 行
      </p>
      <pre className="diff">
        {lines.map((line, index) => (
          <span key={index} className={`diff__line diff__line--${line.op}`}>
            {line.op === 'add' ? '+' : line.op === 'remove' ? '-' : ' '} {line.text}
            {'\n'}
          </span>
        ))}
      </pre>
    </>
  );
}

export default function AdminPromptDetail() {
  const { versions: data } = useLoaderData<LoaderData>();
  const revalidator = useRevalidator();
  const navigate = useNavigate();
  const { prompt, items, defaults, variables, templatable, usedByKey, appended } = data;

  /*
   * `usedByKey` already answers this: it is true when this prompt's key is one
   * a call site resolves - which is precisely what makes it the default every
   * clinic follows unless given another. The server refuses to delete one, and
   * the screen does not offer it, so nobody presses a button to be told no.
   *
   * Read from the payload rather than re-listing the keys here: a use added on
   * the server would otherwise become a default this screen offers to delete.
   */
  const isPlatformDefault = usedByKey;

  const active = items.find((version) => version.status === 'ACTIVE') ?? null;
  /*
   * A version worth resuming, which is NOT simply the newest one.
   *
   * This used to be `items[0]` - newest by version_no, whatever its status - and
   * that put retired wording in the editor. A ROLLED_BACK version is one
   * somebody deliberately took out of service: prefilling from it made the
   * screen show 「generate image of pig」 for the article-image prompt, and made
   * the shipped default invisible for any prompt whose only history was retired
   * versions. Restoring one is what the History tab is for, deliberately and
   * per version.
   *
   * A DRAFT is different - it is unfinished work by definition, and resuming it
   * is the whole point of saving one.
   */
  const resumable = items.find((version) => version.status === 'DRAFT') ?? null;

  /*
   * Editing starts from what is live, then from the newest version, then from
   * the wording compiled into this build. Nobody writes an instruction from a
   * blank page, and a blank first save silently drops the rules the fallback
   * already carries.
   */
  const [body, setBody] = useState(active?.body ?? resumable?.body ?? defaults.system);
  const [template, setTemplate] = useState(
    active?.user_template ?? resumable?.user_template ?? defaults.userTemplate,
  );
  /*
   * The prohibitions, which are editable now and were not.
   *
   * Same three-step start as the body: what is live, then the version being
   * resumed, then the wording this build ships. Clearing it is meaningful -
   * empty releases the shipped block, which is why the box says so rather than
   * simply going blank.
   */
  const [constraints, setConstraints] = useState(
    active?.appended || resumable?.appended || appended || '',
  );
  const [note, setNote] = useState('');
  const [tab, setTab] = useState<'edit' | 'preview' | 'diff' | 'history'>('edit');
  /*
   * What the preview should render. A section prompt defines a format per kind
   * and an image prompt frames a cover differently from an inline picture, so
   * previewing only one of each would leave most of the wording unchecked.
   */
  const [kind, setKind] = useState('body');
  const [slot, setSlot] = useState<'cover' | 'inline'>('cover');

  const [findings, setFindings] = useState<Finding[]>([]);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /*
   * Linted as it is typed, debounced.
   *
   * On the server, not in the browser: the same function decides whether the
   * save is refused, and a second copy of these rules in the client is a second
   * place for them to drift from the ones that are enforced.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      void post<{ findings: Finding[] }>(`/admin/prompts/${prompt.id}/lint`, {
        body,
        userTemplate: template,
        appended: constraints,
      })
        .then((result) => setFindings(result.findings))
        .catch(() => {
          /* A failed lint is not an authoring error; the save still checks. */
        });
    }, 400);
    return () => clearTimeout(timer);
  }, [prompt.id, body, template, constraints]);

  const hasError = findings.some((finding) => finding.level === 'error');
  const dirty =
    body !== (active?.body ?? '') ||
    template !== (active?.user_template ?? '') ||
    constraints !== (active?.appended ?? '');

  const report = (err: unknown, fallback: string) =>
    setError(err instanceof ApiError ? [err.message, ...err.fields].join(' / ') : fallback);

  /*
   * The knobs are passed in rather than read from state: a chip sets state and
   * asks for a preview in the same handler, and reading state there would send
   * the previous value.
   */
  async function loadPreview(forKind = kind, forSlot = slot) {
    setTab('preview');
    setBusy('preview');
    setError(null);
    try {
      setPreview(
        await post<Preview>(`/admin/prompts/${prompt.id}/preview`, {
          body,
          userTemplate: template,
          appended: constraints,
          ...(prompt.taskType === 'section' ? { kind: forKind } : {}),
          ...(isImageTask(prompt.taskType) ? { slot: forSlot } : {}),
        }),
      );
    } catch (err) {
      report(err, 'プレビューを取得できませんでした');
    } finally {
      setBusy(null);
    }
  }

  /**
   * Save, and use it from now on. The only write on this screen.
   *
   * One request: the server lints, refuses only what would not work, writes a
   * new version, and retires the previous one in the same transaction.
   */
  async function save() {
    setBusy('save');
    setError(null);
    setNotice(null);
    try {
      const saved = await post<{ version_no: number; findings: Finding[] }>(
        `/admin/prompts/${prompt.id}/versions`,
        { body, userTemplate: template, appended: constraints, note },
      );
      setNote('');
      const warnings = saved.findings.filter((finding) => finding.level === 'warn').length;
      setNotice(
        `保存しました（v${saved.version_no}）。次の生成からこの指示が使われます。` +
          (warnings > 0 ? `確認事項が${warnings}件あります。` : ''),
      );
      await revalidator.revalidate();
    } catch (err) {
      report(err, '保存できませんでした');
    } finally {
      setBusy(null);
    }
  }

  /** Put an earlier version back. The undo for a save that read badly. */
  async function restore(versionId: string, versionNo: number) {
    setBusy(versionId);
    setError(null);
    setNotice(null);
    try {
      await post(`/admin/prompt-versions/${versionId}/activate`, {});
      setNotice(`v${versionNo} に戻しました。次の生成からこの指示が使われます。`);
      await revalidator.revalidate();
    } catch (err) {
      report(err, '戻せませんでした');
    } finally {
      setBusy(null);
    }
  }

  /**
   * Remove one version from the history.
   *
   * No confirmation dialog, deliberately: what is being deleted is a draft or a
   * superseded wording, the active one has no such button, and the server
   * refuses both the active version and the last remaining one. A modal for
   * every abandoned draft is a modal people learn to dismiss without reading.
   */
  async function removeVersion(versionId: string, versionNo: number) {
    setBusy(`del-${versionId}`);
    setError(null);
    setNotice(null);
    try {
      await del(`/admin/prompts/${prompt.id}/versions/${versionId}`);
      setNotice(`v${versionNo} を削除しました。`);
      await revalidator.revalidate();
    } catch (err) {
      report(err, '削除できませんでした');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <Card
        title={prompt.name}
        caption={`${prompt.key}・${prompt.description}`}
        actions={
          <Link className="btn btn--sm btn--ghost" to="/admin/prompts">
            一覧に戻る
          </Link>
        }
      >
        <p className="muted small" style={{ marginTop: 0 }}>
          使用中: {active ? `v${active.version_no}（${jaDate(active.activated_at)}）` : 'なし'}
          {!active && '。保存されるまで、サーバー内の既定の指示文で動作します。'}
          {!usedByKey && (
            <>
              {' '}
              <Pill tone="warn">未使用</Pill>{' '}
              このキーを参照している処理はありません。保存しても生成には使われません。
            </>
          )}
        </p>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
        {notice && !error && (
          <p className="muted small" role="status">
            {notice}
          </p>
        )}

        <div className="chips" style={{ marginBottom: 12 }}>
          {(
            [
              ['edit', '編集'],
              ['preview', 'プレビュー'],
              ['diff', '使用中との差分'],
              ['history', `履歴（${items.length}）`],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              className="chip"
              aria-pressed={tab === id}
              onClick={() => (id === 'preview' ? void loadPreview() : setTab(id))}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'edit' && (
          <>
            {/*
              The instruction, section by section.

              No variables here on purpose, for every task type but 'image':
              nothing else renders the system message through the template, so a
              {{name}} in it would reach the model as four literal braces - which
              the lint refuses, and which is why the chips belong to the request
              block below and not to this one. An image prompt has no request
              block at all (see lint.ts) - its whole instruction lives here, so
              this is the one case where it is templated.
            */}
            <PromptComposer
              label={isImageTask(prompt.taskType) ? '指示文（画像の作り方）' : '指示文（システムプロンプト）'}
              value={body}
              onChange={setBody}
              shipped={defaults.system}
              variables={isImageTask(prompt.taskType) ? variables : []}
              hint={
                isImageTask(prompt.taskType)
                  ? '実際に画像モデルへ送られる指示です。文字入り・ビフォーアフター・施術風景・実在人物の描写は、この内容に関わらず常に禁止されます。'
                  : 'モデルに常に渡される規則です。承認済み情報の範囲・断定表現の禁止・医療広告ガイドラインへの言及は、下の「確認」に出ます。'
              }
            />

            {templatable ? (
              <PromptComposer
                label="依頼文"
                value={template}
                onChange={setTemplate}
                shipped={defaults.userTemplate}
                variables={variables}
                hint={
                  variables.length === 0
                    ? 'この下に「承認済み情報」が自動で付きます（差し替え不可）。このプロンプトは変数を受け取りません。'
                    : 'この下に「承認済み情報」が自動で付きます（差し替え不可）。'
                }
              />
            ) : (
              <p className="muted small">
                このプロンプトの依頼文は生成のたびに組み立てられるため、ここでは編集できません。
                上の指示文が、このプロンプトで変えられる部分です。
              </p>
            )}

            {/*
              The half nobody may edit, shown rather than described.
              A prohibition that reaches the model and appears on no screen
              reads as a prohibition nobody wrote - which is exactly how this
              screen was read.
            */}
            {/*
              The prohibitions. Editable by JMC, never by a clinic.

              It used to be a read-only <pre> labelled 編集不可, because the text
              was a constant in the server. It is part of the released version
              now (migration 094), so it belongs in a textarea on the screen
              that already claims to own this prompt - and it keeps its own
              place, below the instruction, because it is appended after the
              instruction renders and no {{name}} in it resolves.
            */}
            {appended !== null && (
              <div className="field">
                <label htmlFor="p-appended">常に末尾に追加される内容</label>
                <textarea
                  id="p-appended"
                  rows={6}
                  maxLength={4000}
                  value={constraints}
                  onChange={(event) => setConstraints(event.target.value)}
                />
                <span className="field__hint">
                  上の指示文をどう書いても、この内容がそのあとに必ず付きます。医療広告ガイドライン上の
                  制約なので、削らずに書き換えてください。空にすると既定の内容に戻ります。
                </span>
                {constraints !== appended && (
                  <button
                    type="button"
                    className="btn btn--sm btn--ghost"
                    style={{ marginTop: 6 }}
                    onClick={() => setConstraints(appended)}
                  >
                    既定の内容に戻す
                  </button>
                )}
              </div>
            )}

            <div className="field">
              <label htmlFor="p-note">変更の理由</label>
              <input
                id="p-note"
                maxLength={1000}
                value={note}
                placeholder="例）文字数の指示を明確化"
                onChange={(event) => setNote(event.target.value)}
              />
              <span className="field__hint">履歴に残ります。あとで戻すときの手がかりです。</span>
            </div>

            <p className="form__section">チェック</p>
            <Findings findings={findings} />

            <div className="editpanel__actions">
              <button
                className="btn btn--sm"
                type="button"
                disabled={busy !== null || hasError || body.trim() === ''}
                onClick={() => void save()}
              >
                {busy === 'save' ? '保存中…' : '保存して使う'}
              </button>
              <button
                className="btn btn--ghost btn--sm"
                type="button"
                disabled={busy !== null}
                onClick={() => void loadPreview()}
              >
                プレビュー
              </button>
              {dirty && (
                <span className="muted small" style={{ alignSelf: 'center' }}>
                  未保存の変更があります
                </span>
              )}
            </div>
            <p className="muted small" style={{ marginBottom: 0 }}>
              保存すると、次に作られる記事からこの指示が使われます。以前の版は履歴に残り、
              いつでも戻せます。
            </p>
          </>
        )}

        {tab === 'preview' && (
          <>
            {/*
              Which variant to render. A section prompt carries a format per
              kind and an image prompt frames cover and inline differently, so
              without these only one variant of each was ever checkable.
            */}
            {prompt.taskType === 'section' && (
              <div className="chips" style={{ marginBottom: 12 }}>
                {SECTION_KINDS.map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    className="chip"
                    aria-pressed={kind === id}
                    disabled={busy !== null}
                    onClick={() => {
                      setKind(id);
                      void loadPreview(id, slot);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            {isImageTask(prompt.taskType) && (
              <div className="chips" style={{ marginBottom: 12 }}>
                {(
                  [
                    ['cover', 'カバー画像'],
                    ['inline', '本文中の画像'],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    className="chip"
                    aria-pressed={slot === id}
                    disabled={busy !== null}
                    onClick={() => {
                      setSlot(id);
                      void loadPreview(kind, id);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            {busy === 'preview' ? (
              <p className="muted small">生成中…</p>
            ) : preview === null ? (
              <Empty>プレビューを取得できませんでした。</Empty>
            ) : (
              <>
                <p className="muted small" style={{ marginTop: 0 }}>
                  架空のクリニックの承認済み情報で描画しています（実在クリニックのデータは使いません）。
                  約{preview.tokens.toLocaleString('ja-JP')}トークン
                  {preview.inputMicroCost !== null &&
                    `・入力コスト約 $${(preview.inputMicroCost / 1_000_000).toFixed(4)}`}
                  {preview.model && `・モデル ${preview.model.label}`}
                </p>
                {Object.keys(preview.variables).length > 0 && (
                  <>
                    <p className="form__section">変数の解決結果</p>
                    <ul className="findings">
                      {Object.entries(preview.variables).map(([name, value]) => (
                        <li
                          key={name}
                          className={`findings__item${value === '' ? ' findings__item--warn' : ''}`}
                        >
                          <code className="small">{`{{${name}}}`}</code>
                          <span>
                            {value === '' ? (
                              // An empty variable and a variable nobody wrote look
                              // identical in the rendered prompt; here they do not.
                              <span className="muted">未登録（空欄になります）</span>
                            ) : (
                              value
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                <p className="form__section">システムメッセージ</p>
                <pre className="pre">{preview.system}</pre>
                <p className="form__section">ユーザーメッセージ</p>
                <pre className="pre">{preview.user}</pre>
              </>
            )}
          </>
        )}

        {tab === 'diff' && (
          <>
            <p className="form__section">指示文</p>
            <Diff before={active?.body ?? ''} after={body} />
            {templatable && (
              <>
                <p className="form__section">依頼文</p>
                <Diff before={active?.user_template ?? ''} after={template} />
              </>
            )}
          </>
        )}

        {tab === 'history' && (
          <>
            {items.length === 0 ? (
              <Empty>まだ保存されていません。「編集」から最初の指示文を保存してください。</Empty>
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                {items.map((version) => (
                  <div className="panel" key={version.id}>
                    <h3 className="panel__title">
                      v{version.version_no}{' '}
                      <Pill tone={STATUS_TONE[version.status] ?? 'muted'}>
                        {STATUS_LABEL[version.status] ?? version.status}
                      </Pill>
                    </h3>
                    <p className="muted small" style={{ marginTop: 0 }}>
                      保存：{jaDate(version.created_at)}・{version.created_by_name}
                      {version.activated_at &&
                        `／使用開始：${jaDate(version.activated_at)}・${version.activated_by_name ?? ''}`}
                    </p>
                    {version.note && <p style={{ marginTop: 0 }}>{version.note}</p>}

                    {/*
                      Linted on read, not only when written: the rules move, and a
                      version saved last month may read differently against them
                      now. Advisory here as everywhere else.
                    */}
                    {version.findings.length > 0 && <Findings findings={version.findings} />}

                    <details>
                      <summary className="muted small">内容を表示</summary>
                      <pre className="pre">{version.body}</pre>
                      {version.user_template && <pre className="pre">{version.user_template}</pre>}
                    </details>

                    {version.status !== 'ACTIVE' && (
                      <div className="editpanel__actions">
                        <button
                          className="btn btn--ghost btn--sm"
                          type="button"
                          disabled={busy !== null}
                          onClick={() => void restore(version.id, version.version_no)}
                        >
                          {busy === version.id ? '戻しています…' : 'この版に戻す'}
                        </button>
                        {/*
                          Versions accumulate - an abandoned draft, a wording
                          tried once and rolled back - and the history is worth
                          keeping without being the only option. The active one
                          has no delete: it is what every generation is running.
                        */}
                        {items.length > 1 && (
                          <button
                            className="btn btn--ghost btn--sm"
                            type="button"
                            disabled={busy !== null}
                            onClick={() => void removeVersion(version.id, version.version_no)}
                          >
                            {busy === `del-${version.id}` ? '削除しています…' : 'この版を削除'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Card>

      {/*
        Deletion, and the two cases where it refuses.

        A platform default is what every clinic follows unless told otherwise -
        deleting one would leave that generation with no instruction at all - and
        a prompt a clinic has been given would move that clinic back to the
        default without anybody deciding it should. Both are refused by the
        server, which names the clinics in the second case.
      */}
      <Card
        title="このプロンプトの削除"
        caption="取り消せません。過去の版もすべて削除されます。クリニックが使用中の場合は削除できません。"
      >
        {/*
          Shown either way, and the reason given.

          Hiding it on a platform default meant the catalogue's own prompts -
          which is most of what an operator sees - had no delete control and no
          explanation, which reads as a missing feature rather than a rule.
        */}
        {isPlatformDefault ? (
          <p className="notice" style={{ marginBottom: 0 }}>
            <strong>このプロンプトは削除できません。</strong>
            <br />
            {prompt.key} は、割り当てのないクリニックが既定で使う指示文です。削除すると、この生成に
            使う指示文が無くなり、記事の作成が止まります。内容を変えるときは新しい版を公開してください。
          </p>
        ) : (
          <DangerButton
            label="このプロンプトを削除する"
            confirmText={prompt.name}
            consequences={['このプロンプトの全ての版', 'クリニックへの割り当て候補からの削除']}
            onConfirm={() => del(`/admin/prompts/${prompt.id}`)}
            onDone={() => void navigate('/admin/prompts')}
          />
        )}
      </Card>
    </>
  );
}
